import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import CallRecordingButton from '../CallRecordingButton';
import {
  ThumbsUp, ThumbsDown, RefreshCw, Loader2, X, Quote, AlertTriangle, CheckCircle2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DISC_COLOR = { D: '#EF4444', I: '#F59E0B', S: '#10B981', C: '#3B82F6' };
const DISC_LABEL = {
  D: 'Dominance', I: 'Influence', S: 'Steadiness', C: 'Conscientious',
};

/**
 * DiscReviewModal — inspect a caller's DISC classification, see the evidence
 * quotes the AI keyed off, submit thumbs-up / down feedback (with an optional
 * corrected quadrant), or force a re-run of the tightened prompt.
 */
const DiscReviewModal = ({ callId, onClose, onUpdated }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [corrected, setCorrected] = useState('');
  const [note, setNote] = useState('');
  const [verdictSent, setVerdictSent] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/analytics/caller-intel/${callId}/review`);
      setData(r.data);
      setCorrected('');
      setNote('');
      setVerdictSent(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load review');
      onClose?.();
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [callId]);

  const submitFeedback = async (verdict) => {
    setBusy(true);
    try {
      const body = { verdict };
      if (verdict === 'down') {
        if (corrected) body.corrected_disc = corrected;
        if (note.trim()) body.note = note.trim();
      }
      const r = await axios.post(`${API}/analytics/caller-intel/${callId}/feedback`, body);
      setVerdictSent(verdict);
      toast.success(
        r.data.updated_disc
          ? `Marked incorrect — DISC updated to ${r.data.updated_disc}`
          : verdict === 'up' ? 'Marked accurate — thanks!' : 'Feedback recorded'
      );
      if (r.data.updated_disc) onUpdated?.(r.data.updated_disc);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not submit feedback');
    } finally { setBusy(false); }
  };

  const reanalyze = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/analytics/caller-intel/${callId}/reanalyze`);
      toast.success(`Re-classified as ${r.data.new_disc} (confidence ${r.data.confidence})`);
      onUpdated?.(r.data.new_disc);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Re-analyze failed');
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="disc-review-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-gradient-to-br from-slate-900 to-slate-800 text-white px-5 py-4 flex items-start justify-between" data-force-dark="true">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-300">DISC Review</p>
            <p className="text-sm font-mono mt-0.5 text-slate-100">{callId.slice(0, 32)}…</p>
          </div>
          <button type="button" onClick={onClose} className="text-white/70 hover:text-white" data-testid="disc-review-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-teal-500" /></div>
        ) : !data ? null : (
          <div className="p-5 space-y-4">
            {/* Current classification */}
            <div className="flex items-start gap-3">
              <span
                className="inline-flex items-center justify-center w-14 h-14 rounded-xl text-white text-2xl font-bold shrink-0"
                style={{ background: DISC_COLOR[data.disc] || '#64748B' }}
                data-testid="disc-current"
              >{data.disc || '?'}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-slate-900">
                  {DISC_LABEL[data.disc] || 'Unknown'} · <span className="font-normal text-slate-500">confidence {data.disc_confidence}%</span>
                </p>
                {data.manually_corrected && (
                  <p className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 mt-0.5">
                    <CheckCircle2 className="w-3 h-3" /> manually corrected
                  </p>
                )}
                <p className="text-xs text-slate-600 mt-1">{data.disc_evidence || 'No evidence sentence recorded.'}</p>
              </div>
            </div>

            {/* Evidence quotes — each with a play-from-here chip if we
                could estimate a timestamp for it */}
            {(data.evidence_snippets_with_time?.length || data.evidence_snippets?.length) > 0 && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="disc-evidence-snippets">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2 inline-flex items-center gap-1">
                  <Quote className="w-3 h-3" /> Evidence from transcript
                </p>
                <ul className="space-y-1.5">
                  {(data.evidence_snippets_with_time || (data.evidence_snippets || []).map(q => ({ quote: q, start_ts: null }))).map((entry, i) => (
                    <li
                      key={i}
                      className="text-xs text-slate-700 italic border-l-2 border-slate-300 pl-2 flex items-start gap-2"
                      data-testid={`evidence-snippet-${i}`}
                    >
                      <span className="flex-1">&ldquo;{entry.quote}&rdquo;</span>
                      {entry.start_ts != null && (
                        <span
                          className="not-italic shrink-0"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <CallRecordingButton
                            callId={data.call_id || callId}
                            startTime={entry.start_ts}
                            size="xs"
                            label={`${Math.floor(entry.start_ts / 60)}:${(entry.start_ts % 60).toString().padStart(2, '0')}`}
                          />
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Counter-evidence */}
            {data.disc_counter_evidence && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 flex items-start gap-2" data-testid="disc-counter-evidence">
                <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-700">Counter-signal</p>
                  <p className="text-xs text-slate-700 mt-0.5">{data.disc_counter_evidence}</p>
                </div>
              </div>
            )}

            {/* Feedback */}
            <div className="rounded-xl border border-slate-200 p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Is this classification right?</p>
              {verdictSent ? (
                <p className="text-xs text-emerald-700 font-semibold">✓ Feedback recorded — thanks!</p>
              ) : (
                <>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => submitFeedback('up')}
                      disabled={busy}
                      className="flex-1 text-xs font-semibold border border-emerald-200 bg-emerald-50 text-emerald-800 rounded-lg py-2 hover:bg-emerald-100 disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
                      data-testid="disc-thumbs-up"
                    >
                      <ThumbsUp className="w-3.5 h-3.5" /> Accurate
                    </button>
                    <button
                      type="button"
                      onClick={() => submitFeedback('down')}
                      disabled={busy || !corrected}
                      className="flex-1 text-xs font-semibold border border-rose-200 bg-rose-50 text-rose-800 rounded-lg py-2 hover:bg-rose-100 disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
                      data-testid="disc-thumbs-down"
                    >
                      <ThumbsDown className="w-3.5 h-3.5" /> Wrong
                    </button>
                  </div>
                  <div className="mt-2">
                    <p className="text-[10px] text-slate-500 mb-1">If wrong, pick the correct quadrant:</p>
                    <div className="grid grid-cols-4 gap-1">
                      {['D', 'I', 'S', 'C'].map((k) => (
                        <button
                          key={k}
                          type="button"
                          onClick={() => setCorrected((c) => c === k ? '' : k)}
                          className={`text-xs font-bold py-1.5 rounded-md border transition-colors ${
                            corrected === k ? 'text-white' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                          }`}
                          style={corrected === k ? { background: DISC_COLOR[k], borderColor: DISC_COLOR[k] } : undefined}
                          data-testid={`disc-corrected-${k}`}
                        >{k}</button>
                      ))}
                    </div>
                  </div>
                  <textarea
                    className="w-full mt-2 text-xs border border-slate-200 rounded-lg p-2 resize-none focus:outline-none focus:ring-2 focus:ring-teal-400"
                    rows={2}
                    placeholder="Optional: what did the AI miss?"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    maxLength={500}
                    data-testid="disc-feedback-note"
                  />
                </>
              )}
            </div>

            {/* Reanalyze */}
            <button
              type="button"
              onClick={reanalyze}
              disabled={busy}
              className="w-full text-xs font-semibold text-teal-800 border border-teal-200 bg-teal-50 hover:bg-teal-100 rounded-lg py-2 disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
              data-testid="disc-reanalyze"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Re-run tightened DISC prompt
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DiscReviewModal;
